import * as z from "zod";
import { code, codeName, flag, pagination } from "../index";
import { inboundStatuses, poStatuses, receiptOutcomes } from "./enums";

// "Inbound atteso e ricevuto" (spec v0.2 §9, P8.10a, ADR-214): the POs coming in, what was received against what was
// expected, the open discrepancies and the POs received in part. A view of Tomorrow, served by procurement, which
// owns POs and inbounds (ADR-082); it stands next to "PO per fornitore" (`listSupplierPurchaseTotals`), which counts.

/**
 * Where the goods of a PO stand, from its lines and its inbounds:
 * - `awaiting_shipment`: nothing received, and no inbound with the warehouse yet;
 * - `in_transit`: nothing received, an inbound announced to the warehouse (from its DDT on);
 * - `partially_received`: units received, units still to receive;
 * - `received`: nothing left to receive on its open lines.
 */
export const inboundProgressStages = ["awaiting_shipment", "in_transit", "partially_received", "received"] as const;
export type InboundProgressStage = (typeof inboundProgressStages)[number];

/**
 * An open discrepancy of an inbound:
 * - `exception`: the three-way check failed, Logistics writes the packing list again or sends it back (ADR-131);
 * - `to_resolve`: received with a difference Logistics has not confirmed (ADR-139);
 * - `to_settle`: units missing, confirmed, waiting for the choice of the supplier (ADR-140).
 */
export const inboundDiscrepancies = ["exception", "to_resolve", "to_settle"] as const;
export type InboundDiscrepancy = (typeof inboundDiscrepancies)[number];

/** The inbounds a row carries at most, those with a discrepancy first: the rest are counted. */
export const INBOUND_PROGRESS_INBOUNDS = 20;

/**
 * The POs coming in: confirmed, or closed with a discrepancy still open. The earliest delivery window first.
 */
export const listInboundProgressInput = z.object({
  /** Code of the season. */
  season: code.optional(),
  supplier: z.uuid().optional(),
  brand: z.uuid().optional(),
  stage: z.enum(inboundProgressStages).optional(),
  /** Only the POs with an open discrepancy on one of their inbounds. */
  discrepancies: flag.optional(),
  /** Part of the number of the PO, by its letters and digits (ADR-117). */
  q: z.string().trim().min(1).max(30).optional(),
  ...pagination({ defaultSize: 50, maxSize: 200 }),
});
export type ListInboundProgressInput = z.input<typeof listInboundProgressInput>;

/** An inbound of the PO, sent and not cancelled, as the row shows it. */
export const inboundProgressInboundDto = z.object({
  id: z.uuid(),
  inboundNumber: z.string(),
  status: z.enum(inboundStatuses),
  isExtraUnits: z.boolean(),
  expectedDeliveryDate: z.iso.date().nullable(),
  /** The units its lines declare. */
  qtyDeclared: z.number().int(),
  /** The units the warehouse received on its lines. */
  qtyReceived: z.number().int(),
  receiptOutcome: z.enum(receiptOutcomes).nullable(),
  discrepancy: z.enum(inboundDiscrepancies).nullable(),
});
export type InboundProgressInboundDto = z.output<typeof inboundProgressInboundDto>;

/** A PO coming in: what it orders, what its inbounds announce and carry, what arrived, and what is open. */
export const inboundProgressDto = z
  .object({
    id: z.uuid(),
    poNumber: z.string(),
    status: z.enum(poStatuses),
    brandOrgId: z.uuid(),
    brand: z.string(),
    supplierOrgId: z.uuid(),
    supplier: z.string(),
    season: z.string().nullable(),
    destination: codeName.nullable(),
    deliveryWindowOpen: z.iso.date().nullable(),
    deliveryWindowClose: z.iso.date().nullable(),
    stage: z.enum(inboundProgressStages),
    /** The units of its lines not cancelled: the confirmed quantity where there is one. */
    qtyOrdered: z.number().int(),
    /** What its inbounds announce, less the missing units the supplier ships later (V-02, ADR-140). */
    qtyAnnounced: z.number().int(),
    /** Declared and not received yet on the inbounds the warehouse has (from the DDT on). */
    qtyInTransit: z.number().int(),
    qtyReceived: z.number().int(),
    /** Still to receive on its open lines. */
    qtyToReceive: z.number().int(),
    /** Its inbounds sent and not cancelled. */
    inboundCount: z.number().int(),
    /** Its inbounds with an open discrepancy. */
    discrepancyCount: z.number().int(),
    /** The earliest expected delivery of an inbound not received yet. */
    nextExpectedDelivery: z.iso.date().nullable(),
    /** At most `INBOUND_PROGRESS_INBOUNDS`, those with a discrepancy first, then by number. */
    inbounds: z.array(inboundProgressInboundDto),
  })
  .meta({ id: "InboundProgress" });
export type InboundProgressDto = z.output<typeof inboundProgressDto>;
